import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useFormik } from "formik";
import * as yup from "yup";
import { useResetPasswordMutation } from "../../../app/authApiSlice";
import toast from "../../../helpers/toast";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [resetPassword, { isLoading, error }] = useResetPasswordMutation();

  const formik = useFormik({
    initialValues: { new_password: "", confirm_password: "" },
    validationSchema: yup.object({
      new_password: yup
        .string()
        .min(8, "Must be at least 8 characters")
        .required("Required"),
      confirm_password: yup
        .string()
        .oneOf([yup.ref("new_password")], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (values) => {
      try {
        await resetPassword({ token, new_password: values.new_password }).unwrap();
        toast.success("Password reset successfully. Please log in.");
        navigate("/login");
      } catch (err) {
        // Error is displayed via the `error` state below.
      }
    },
  });

  if (!token) {
    return (
      <Box sx={{ minHeight: "100vh", background: "#eee", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <StyledPaper>
          <Box sx={{ p: 4 }}>
            <Alert severity="error">
              Invalid or missing reset token.{" "}
              <Link to="/forgot-password" style={{ color: "#27ae60" }}>
                Request a new link
              </Link>
            </Alert>
          </Box>
        </StyledPaper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#eee",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& form > *": { mb: 2.5 },
        "& .heading": {
          mt: 2,
          color: "#27ae60",
          fontWeight: "bolder",
          fontVariant: "smallCaps",
          fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
          fontSize: "2.5rem",
        },
        "& .subHeading": { color: "#9faecb" },
        "& button": {
          background: "#27ae60",
          "& :hover": { background: "#27ae60" },
        },
      }}
    >
      <Stack alignItems="center" spacing={2} sx={{ width: "100%" }}>
        <StyledPaper>
          <Box className="paperBody" sx={{ p: 4 }}>
            <Box component="form" onSubmit={formik.handleSubmit}>
              <Typography variant="h1" className="heading">
                RESET PASSWORD
              </Typography>
              <Typography className="subHeading">
                Choose a strong new password for your account.
              </Typography>
              <FormControl error fullWidth>
                <StyledTextField
                  autoFocus
                  type="password"
                  variant="outlined"
                  id="new_password"
                  name="new_password"
                  placeholder="New password"
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                  error={formik.touched.new_password && Boolean(formik.errors.new_password)}
                />
                <FormHelperText>
                  {formik.touched.new_password && formik.errors.new_password}
                </FormHelperText>
              </FormControl>
              <FormControl error fullWidth>
                <StyledTextField
                  type="password"
                  variant="outlined"
                  id="confirm_password"
                  name="confirm_password"
                  placeholder="Confirm new password"
                  value={formik.values.confirm_password}
                  onChange={formik.handleChange}
                  error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                />
                <FormHelperText>
                  {formik.touched.confirm_password && formik.errors.confirm_password}
                </FormHelperText>
                {error && (
                  <FormHelperText error>
                    {error?.data?.message || error?.message}
                  </FormHelperText>
                )}
              </FormControl>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                {isLoading ? (
                  <CircularProgress />
                ) : (
                  <Button type="submit" variant="contained">
                    Reset Password
                  </Button>
                )}
                <Typography variant="body2">
                  <Link to="/login" style={{ color: "#27ae60" }}>
                    Back to Login
                  </Link>
                </Typography>
              </Stack>
            </Box>
          </Box>
        </StyledPaper>
      </Stack>
    </Box>
  );
}

export default ResetPassword;
